declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

export const isAnalyticsEnabled = (): boolean => {
  return typeof window !== 'undefined' && !!GA_MEASUREMENT_ID && !!window.gtag;
};

export const trackPageView = (url: string, title?: string): void => {
  if (!isAnalyticsEnabled()) return;
  window.gtag('config', GA_MEASUREMENT_ID!, {
    page_path: url,
    page_title: title,
  });
};

export const trackEvent = (
  eventName: string,
  eventParams: Record<string, any> = {}
): void => {
  if (!isAnalyticsEnabled()) return;
  window.gtag('event', eventName, eventParams);
};

// Consultation request tracking
export const trackConsultationRequest = (params: {
  consultationType: string;
  industry?: string;
  companySize?: string;
  region?: string;
  step?: number;
}): void => {
  trackEvent('consultation_request', {
    event_category: 'conversion',
    event_label: params.consultationType,
    consultation_type: params.consultationType,
    industry: params.industry,
    company_size: params.companySize,
    region: params.region,
    form_step: params.step,
  });
};

export const trackConsultationFormStep = (step: number, stepName: string): void => {
  trackEvent('consultation_form_step', {
    event_category: 'engagement',
    event_label: stepName,
    form_step: step,
    step_name: stepName,
  });
};

export const trackConsultationSubmit = (params: {
  consultationType: string;
  industry: string;
  companySize: string;
  region: string;
}): void => {
  trackEvent('generate_lead', {
    event_category: 'conversion',
    event_label: 'consultation_form_submit',
    consultation_type: params.consultationType,
    industry: params.industry,
    company_size: params.companySize,
    region: params.region,
    value: 1,
  });
};

// Assessment tracking
export const trackAssessmentStart = (role: string): void => {
  trackEvent('assessment_start', {
    event_category: 'engagement',
    event_label: role,
    role_selected: role,
  });
};

export const trackAssessmentSectionComplete = (section: string, sectionIndex: number): void => {
  trackEvent('assessment_section_complete', {
    event_category: 'engagement',
    event_label: section,
    section_name: section,
    section_index: sectionIndex,
  });
};

export const trackAssessmentComplete = (params: {
  role: string;
  score: number;
  readinessLevel: string;
}): void => {
  trackEvent('assessment_complete', {
    event_category: 'conversion',
    event_label: params.readinessLevel,
    role: params.role,
    score: params.score,
    readiness_level: params.readinessLevel,
    value: 1,
  });
};

// CTA tracking
export const trackCTAClick = (ctaName: string, location: string, destination?: string): void => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_name: ctaName,
    cta_location: location,
    destination_url: destination,
  });
};

// ROI Calculator tracking
export const trackROICalculation = (params: {
  industry?: string;
  employeeCount?: number;
  estimatedROI?: number;
}): void => {
  trackEvent('roi_calculation', {
    event_category: 'engagement',
    event_label: 'roi_calculator',
    industry: params.industry,
    employee_count: params.employeeCount,
    estimated_roi: params.estimatedROI,
  });
};

// Page-specific tracking
export const trackPageEngagement = (pageName: string, action: string, label?: string): void => {
  trackEvent('page_engagement', {
    event_category: pageName,
    event_label: label || action,
    action,
  });
};
